import { supabase } from '@/integrations/supabase/client';
import type { PermissionGrid } from '@/utils/roleBasedAccess';
import { EMPTY_PERMISSION_GRID } from '@/utils/roleBasedAccess';

export interface PendingInvitation {
  id: string;
  email: string;
  permissions: PermissionGrid;
  invited_at: string;
  invited_by: string | null;
  expires_at: string;
  status: string;
  invitation_token: string;
}

export const generateSetupUrl = (token: string): string => {
  const baseUrl = typeof globalThis !== 'undefined' && globalThis.location 
    ? globalThis.location.origin 
    : '';
  return `${baseUrl}/admin/setup?token=${token}`;
};


export interface AdminUserWithPermissions {
  user_id: string;
  email: string;
  created_at: string;
  created_by?: string;
  permissions: PermissionGrid;
  last_sign_in_at?: string | null;
  email_confirmed_at?: string | null;
  created_at_auth?: string;
}

export const getAdminUsersWithPermissions = async (): Promise<{ data: AdminUserWithPermissions[]; error: unknown }> => {
  try {
    const { data: rows, error } = await supabase.rpc('get_admin_users_with_permissions' as any);
    if (error) return { data: [], error };
    if (!rows || rows.length === 0) return { data: [], error: null };

    const byUser = new Map<string, AdminUserWithPermissions>();
    for (const row of rows as { user_id: string; email: string; created_at: string; created_by: string | null; page: keyof PermissionGrid; level: PermissionGrid[keyof PermissionGrid] }[]) {
      if (!byUser.has(row.user_id)) {
        byUser.set(row.user_id, {
          user_id: row.user_id,
          email: row.email,
          created_at: row.created_at,
          created_by: row.created_by ?? undefined,
          permissions: { ...EMPTY_PERMISSION_GRID },
        });
      }
      byUser.get(row.user_id)!.permissions[row.page] = row.level;
    }

    const { data: { user: currentUser } } = await supabase.auth.getUser();
    // The RPC has no ORDER BY, so preserve the previous newest-first table ordering here.
    const sortedUsers = Array.from(byUser.values()).sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const enriched = sortedUsers.map((adminUser) => {
      if (currentUser && adminUser.user_id === currentUser.id) {
        return { ...adminUser, last_sign_in_at: currentUser.last_sign_in_at, email_confirmed_at: currentUser.email_confirmed_at, created_at_auth: currentUser.created_at };
      }
      return { ...adminUser, last_sign_in_at: null, email_confirmed_at: null, created_at_auth: adminUser.created_at };
    });

    return { data: enriched, error: null };
  } catch (error) {
    return { data: [], error };
  }
};

/**
 * Validate and sanitize email input
 */
const validateAndSanitizeEmail = (email: string): { success: true; sanitizedEmail: string } | { success: false; error: Error } => {
  if (!email || typeof email !== 'string') {
    return { success: false, error: new Error('Email is required and must be a string') };
  }

  // Check email length to prevent abuse
  if (email.length > 254) { // RFC 5321 limit
    return { success: false, error: new Error('Email address is too long') };
  }

  // Sanitize and validate email format
  const sanitizedEmail = email.toLowerCase().trim();
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!emailRegex.test(sanitizedEmail)) {
    return { success: false, error: new Error('Please provide a valid email address') };
  }

  // Additional security: prevent suspicious patterns
  if (sanitizedEmail.includes('..') || sanitizedEmail.startsWith('.') || sanitizedEmail.endsWith('.')) {
    return { success: false, error: new Error('Invalid email format detected') };
  }

  return { success: true, sanitizedEmail };
};

interface CreateAdminInvitationRpcResult {
  success: boolean;
  message?: string;
  invitation_token?: string;
}

/**
 * SIMPLIFIED APPROACH: Create setup token for any admin email
 * This allows super admins to create setup links for any email address
 */
export const createAdminSetupToken = async (email: string, permissions: PermissionGrid) => {
  try {

    // Validate and sanitize email
    const emailValidation = validateAndSanitizeEmail(email);
    if (!emailValidation.success) {
      // Type assertion: we know error exists when success is false
      return { success: false, error: (emailValidation as { success: false; error: Error }).error };
    }

    const sanitizedEmail = emailValidation.sanitizedEmail;

    // Security-critical: invitation creation and rate limiting are enforced server-side.
    const { data: rpcData, error: rpcError } = await supabase.rpc('create_admin_invitation' as any, {
      p_email: sanitizedEmail,
      p_permissions: permissions,
    });

    if (rpcError) {
      return {
        success: false,
        error: new Error(`Failed to create setup token: ${rpcError.message || 'Unknown error'}`),
      };
    }

    const rpcResult = rpcData as CreateAdminInvitationRpcResult | null;
    if (!rpcResult?.success || !rpcResult.invitation_token) {
      return {
        success: false,
        error: new Error(rpcResult?.message || 'Failed to create setup token'),
      };
    }

    const setupToken = rpcResult.invitation_token;
    const setupUrl = generateSetupUrl(setupToken);

    return { 
      success: true, 
      data: {
        setupToken,
        setupUrl,
        email: sanitizedEmail,
        permissions
      },
      message: `Setup link created for ${sanitizedEmail}`
    };

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    return { 
      success: false, 
      error: new Error(`Unexpected error: ${errorMessage}`) 
    };
  }
};

/**
 * Complete admin setup - called after user creates password via setup link
 * This links the authenticated user to the admin record
 */
export const completeAdminSetup = async (exchangeCode: string): Promise<{
  success: boolean;
  error?: Error;
  message?: string;
}> => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      return { success: false, error: new Error('Not authenticated. Please try signing in again.') };
    }

    const { data, error } = await supabase.rpc('complete_admin_setup_by_code' as any, {
      p_exchange_code: exchangeCode,
    });

    if (error) {
      return { success: false, error: new Error(error.message || 'Failed to complete admin setup.') };
    }

    const result = data as { success: boolean; message?: string } | null;

    if (!result?.success) {
      return { success: false, error: new Error(result?.message || 'Failed to complete admin setup.') };
    }

    return { success: true, message: result.message };
  } catch {
    return { success: false, error: new Error('An unexpected error occurred during setup.') };
  }
};

/**
 * Get pending invitations to show in admin management
 */
export const getPendingInvitations = async () => {
  try {
    const { data, error } = await supabase
      .from('admin_invitations')
      .select('*')
      .eq('status', 'pending')
      .order('invited_at', { ascending: false });

    if (error) {
      return { data: [] as PendingInvitation[], error };
    }

    return { data: (data || []) as unknown as PendingInvitation[], error: null };
  } catch (error) {
    return { data: [] as PendingInvitation[], error };
  }
};



/**
 * Cancel an invitation
 */
export const cancelInvitation = async (invitationId: string) => {
  try {
    const { error } = await supabase
      .from('admin_invitations')
      .update({ status: 'cancelled' })
      .eq('id', invitationId);

    if (error) {
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    return { success: false, error };
  }
};

export const getMyPermissions = async (signal?: AbortSignal): Promise<PermissionGrid> => {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError) throw userError;
  if (!user) return { ...EMPTY_PERMISSION_GRID };

  const resolvedSignal = signal ?? (() => {
    const c = new AbortController();
    setTimeout(() => c.abort(), 5000);
    return c.signal;
  })();

  const { data, error } = await supabase
    .rpc('get_my_permissions' as any)
    .abortSignal(resolvedSignal);

  // A lookup failure (timeout/network/RPC error) must not be treated as a
  // legitimate empty grid — callers need to distinguish "no access" from
  // "couldn't check", otherwise transient failures sign out valid admins.
  if (error) throw error;
  if (!data) return { ...EMPTY_PERMISSION_GRID };

  const grid = { ...EMPTY_PERMISSION_GRID };
  for (const row of data as { page: keyof PermissionGrid; level: PermissionGrid[keyof PermissionGrid] }[]) {
    grid[row.page] = row.level;
  }
  return grid;
};

/**
 * Invite a new admin user
 */


export const updateAdminPermissions = async (userId: string, grid: PermissionGrid) => {
  try {
    const { data, error } = await supabase.rpc('update_admin_permissions' as any, {
      p_user_id: userId,
      p_permissions: grid,
    });
    if (error) return { success: false, error };
    return { success: true, data, error: null };
  } catch (error) {
    return { success: false, error };
  }
};

/**
 * Remove admin user
 */
export const removeAdminUser = async (userId: string) => {
  try {
    const { error } = await supabase
      .from('admin_users')
      .delete()
      .eq('user_id', userId);

    if (error) {
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    return { success: false, error };
  }
};